/* Full Patch product forms only. Native variant/AJAX callbacks alone enable ATC. */
(() => {
  'use strict';
  if (window.GzPatchAvailability) return;
  const rootSelector = '[data-gz-patch-availability]';
  const instances = new Map();
  const toggle = (element, name, value) => {
    if (element.classList.contains(name) !== value) element.classList.toggle(name, value);
  };

  function mount(root) {
    if (instances.has(root)) return instances.get(root);
    let product;
    try { product = JSON.parse(root.dataset.product); } catch (_) { return; }
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return;
    const form = root.querySelector('form');
    const master = form && form.querySelector('[data-variants][name="id"]');
    const button = form && form.querySelector('.add_to_cart');
    if (!master || !button) return;
    let controller;
    let queued = false;
    let destroyed = false;
    let nativeVariant;
    const timers = new Set();
    const paymentInert = new Map();
    const allUnavailable = product.variants.every(variant => variant.available !== true);
    const busy = () => Boolean(button.querySelector('.zoomOut, .checkmark-active'));

    function selection() {
      let values;
      const selects = Array.from(root.querySelectorAll('.single-option-selector'));
      const swatches = Array.from(root.querySelectorAll('.swatch[data-option-index]'));
      if (root.dataset.product_form_style === 'radio' && swatches.length) {
        values = swatches.map(group => group.querySelector('input[type="radio"]:checked')?.value || '');
      } else if (selects.length) {
        values = selects.map(select => select.value);
      } else if (product.variants.length > 1) {
        return {complete: false, allowed: false, variant: null};
      }
      const complete = !values || (values.length === product.options.length && values.every(value => value !== ''));
      const variant = complete ? product.variants.find(item => String(item.id) === master.value && (!values || item.options.every((value, index) => String(value) === values[index]))) : null;
      // The emitted native object is authoritative after a matching callback.
      const available = nativeVariant === undefined ? variant?.available === true : Boolean(nativeVariant && variant && String(nativeVariant.id) === String(variant.id) && nativeVariant.available === true);
      return {complete, variant, allowed: Boolean(complete && variant && available && !master.disabled)};
    }

    function reconcile() {
      const state = selection();
      const pending = busy();
      if (controller && controller.flags.is_form_valid !== state.allowed) controller.flags.is_form_valid = state.allowed;
      toggle(root, 'product-form--is-valid', state.allowed);
      toggle(button, 'make-selection', !state.complete && !allUnavailable);
      // Never clear disabled or replace the label of an available/busy button.
      if (!state.allowed || pending) {
        if (!button.disabled) button.disabled = true;
        toggle(button, 'disabled', true);
      }
      const ariaDisabled = String(button.disabled || !state.allowed || pending);
      if (button.getAttribute('aria-disabled') !== ariaDisabled) button.setAttribute('aria-disabled', ariaDisabled);
      if (!state.allowed && !pending) {
        const translation = window.PXUTheme?.translation || {};
        const label = allUnavailable || (state.complete && state.variant)
          ? (translation.sold_out_text || 'Sold out')
          : (!state.complete ? (button.dataset.label_make_selection || 'Make a selection') : (translation.unavailable_text || 'Unavailable'));
        const text = button.querySelector('.text');
        if (text && text.textContent !== label) text.textContent = label;
      }
      root.querySelectorAll('.shopify-payment-button').forEach(payment => {
        if (!paymentInert.has(payment)) paymentInert.set(payment, payment.inert);
        const inert = !state.allowed || pending || paymentInert.get(payment);
        if (payment.inert !== inert) payment.inert = inert;
      });
    }

    function schedule() {
      if (queued || destroyed) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        if (!destroyed && root.isConnected) reconcile();
      });
    }

    function attach(data) {
      if (controller || !data || typeof data.UpdateAtcBtn !== 'function') return;
      controller = data;
      // Keep native Alpine selection preparation and watchers, not its enable-by-completeness bug.
      data.UpdateAtcBtn = function () { reconcile(); schedule(); };
      const prepare = data.PrepareForm;
      data.PrepareForm = function () {
        const result = prepare.apply(this, arguments);
        schedule();
        // Existing PrepareForm resets select.value without a change event at 50/500/1000 ms.
        [60, 510, 1010].forEach(delay => {
          const timer = setTimeout(() => { timers.delete(timer); schedule(); }, delay);
          timers.add(timer);
        });
        return result;
      };
      schedule();
    }

    const onVariant = event => {
      if (event.detail?.selector?.variantIdField !== master) return;
      nativeVariant = event.detail.variant;
      // The event precedes the native callback's final button writes.
      schedule();
    };
    const observer = new MutationObserver(schedule);
    observer.observe(root, {subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'disabled', 'selected']});
    root.addEventListener('change', schedule);
    master.addEventListener('change', schedule);
    document.addEventListener('variant:change', onVariant);
    const instance = {
      attach,
      purchaseAllowed: () => selection().allowed && !busy() && !button.disabled && !button.classList.contains('disabled'),
      reconcile,
      destroy() {
        destroyed = true;
        timers.forEach(clearTimeout);
        observer.disconnect();
        root.removeEventListener('change', schedule);
        master.removeEventListener('change', schedule);
        document.removeEventListener('variant:change', onVariant);
        paymentInert.forEach((inert, payment) => { payment.inert = inert; });
        instances.delete(root);
      }
    };
    instances.set(root, instance);
    schedule();
    return instance;
  }

  function purchaseGuard(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || (event.type === 'click' && !target.closest('.add_to_cart, .shopify-payment-button'))) return;
    const root = target.closest(rootSelector);
    if (!root) return;
    const instance = instances.get(root) || mount(root);
    if (!instance || !instance.purchaseAllowed()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      instance?.reconcile();
    }
  }
  window.GzPatchAvailability = {attach(root, data) { mount(root)?.attach(data); }};
  document.addEventListener('click', purchaseGuard, true);
  document.addEventListener('submit', purchaseGuard, true);
  function scan() {
    document.querySelectorAll(rootSelector).forEach(root => {
      const instance = mount(root);
      // Late asset/section loading remains safe if x-init ran before this module.
      if (instance && root.getAttribute('x-data') === 'ProductForm' && window.Alpine?.$data) {
        instance.attach(window.Alpine.$data(root));
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, {once: true});
  else scan();
  document.addEventListener('alpine:initialized', scan);
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => { if (event.target.contains(root)) instance.destroy(); });
  });
})();
